import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { Helper, RCFieldView } from '@app/shared/utils';
import { IconName } from '@app/shared/utils/icon/icons';
import { LoaderComponent } from '../../../core/components/loader/loader.component';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '@app/shared/pipes';
import { RcLoaderSize } from '@rc-df/ds-web-components';

@Component({
  selector: 'app-rc-field-action',
  templateUrl: './rc-field-action.component.html',
  styleUrls: ['./rc-field-action.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RcFieldActionComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  imports: [FormsModule, LoaderComponent, NgTemplateOutlet, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RcFieldActionComponent implements ControlValueAccessor, OnChanges {
  @Input() public enableLibInputNumber = true;

  @Input() public placeholder = '';
  @Input() public label = '';
  @Input() public view = '';
  @Input() public required: boolean;
  @Input() public isDisabled: boolean;
  @Input() public showDisabledState = true;
  @Input() public accessible = true;
  @Input() public minlength: number;
  @Input() public maxlength: number;
  @Input() public pattern: string;
  @Input() public min: number;
  @Input() public max: number;
  @Input() public step: number;
  @Input() public badge: string | number;
  @Input() public hint: string;
  @Input() public error: string;
  @Input() public integersOnly: boolean;
  @Input() public value: number;
  @Input() public readingDirection: 'ltr' | 'rtl' | null = null;

  @ViewChild('input', { static: true }) public input: ElementRef<HTMLInputElement>;

  @Output() public focusInput = new EventEmitter();
  @Output() public blurInput = new EventEmitter();
  @Output() public readonlyInput = new EventEmitter();
  @Output() public submitValue = new EventEmitter();
  @Output() public cancelValue = new EventEmitter();

  /***
   * @description references a custom error template passed from a parent component
   * via content projection.
   */
  @ContentChild('customError', { static: false }) customErrorTemplate: TemplateRef<unknown>;

  public displayValue: string = '';
  public localValue: number;
  public iconName = IconName;
  public rcLoaderSize: RcLoaderSize = 'small';
  public readonly rcFieldView = RCFieldView;

  public compositionStarted = false;

  public get isLoading(): boolean {
    return this._isLoading;
  }
  public get readonly(): boolean {
    return this._readonly;
  }
  @Input() public set isLoading(value: boolean) {
    this._isLoading = value;
    if (!value) {
      this._readonly = true;
      this.isFocused = false;
    }
  }
  @Input() public set readonly(value: boolean) {
    this._readonly = value;
    if (!value) {
      this.input.nativeElement.focus();
      this.isFocused = true;
    }
  }

  private isFocused = false;
  private cdr = inject(ChangeDetectorRef);
  private _readonly: boolean;
  private _isLoading: boolean;

  /**
   * Invoked when the model has been changed
   */
  public onChange: (_: unknown) => void = () => undefined;

  /**
   * Invoked when the model has been touched
   */
  public onTouched: () => void = () => undefined;

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.value) {
      this.writeValue(changes.value.currentValue);
      this.localValue = changes.value.currentValue;
    }
  }

  /**
   * Method that is invoked on compositionstart and compositionend inputs events, they are called when using cyrillic alphabets only
   * we want to make sure that the input looks active (design issue) when starting to write symbol
   */
  public setCompositionStarted(isStarted: boolean) {
    this.compositionStarted = isStarted;
  }

  public isActive(): boolean {
    return this.compositionStarted || !!this.value || this.value === 0 || this.isFocused;
  }

  public onFocus(event: FocusEvent) {
    this.focusInput.emit(event);
    this.isFocused = true;
  }
  public onBlur(event: FocusEvent) {
    this.blurInput.emit(event);
    this.isFocused = false;
  }

  /**
   * Method that is invoked on an update of a model.
   */
  public updateChanges() {
    this.onChange(this.value);
  }

  public updateValue(value: string | number): void {
    this.displayValue = value?.toString();
    this.value = this.validateMinValue(Helper.sanitizeAndRound(value));

    if (this.input && this.input.nativeElement) {
      this.input.nativeElement.value = this.displayValue;
    }

    this.updateChanges();
  }

  /**
   * Writes a new item to the element.
   * @param value the value
   */
  public writeValue(value: number): void {
    const roundValue = value ? Number(value.toFixed(2)) : value;
    this.value = this.validateMinValue(roundValue);
    this.localValue = this.validateMinValue(roundValue);

    this.displayValue = this.value !== null && this.value !== undefined ? this.value.toString() : '';

    if (this.input && this.input.nativeElement) {
      this.input.nativeElement.value = this.displayValue;
    }

    this.cdr.detectChanges();
  }

  /**
   * Registers a callback function that should be called when the control's value changes in the UI.
   */
  public registerOnChange(fn: () => unknown): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function that should be called when the control receives a blur event.
   */
  public registerOnTouched(fn: () => unknown): void {
    this.onTouched = fn;
  }

  public onEdit(edit: boolean, disabled: boolean): void {
    if (disabled) {
      return;
    }

    this._readonly = !edit;
    this.readonlyInput.emit(this._readonly);
    if (edit) {
      this.input.nativeElement.focus();
    } else {
      this.updateValue(this.localValue);
      if (this.localValue) {
        this.updateChanges();
      }
      this.input.nativeElement.blur();
      this.cancelValue.emit();
    }
    this.isFocused = edit;
  }

  public onSubmit(): void {
    if (this.localValue !== this.value) {
      this.isFocused = false;
      this.localValue = this.value;
      this._isLoading = true;
      this.submitValue.emit();
    } else {
      this._readonly = true;
      this.readonlyInput.emit(this._readonly);
      this.isFocused = false;
    }
  }

  public onKeyDown(event: KeyboardEvent): void {
    Helper.priceInputOnKeyDown(event, (value) => this.updateValue(value));
  }

  public onPaste(event: ClipboardEvent): void {
    Helper.priceInputOnPaste(event, (value) => this.updateValue(value));
  }

  private validateMinValue(value: number): number {
    return value >= this.min ? value : this.min;
  }
}
